"use client";

import { useEffect, useMemo, useState } from "react";

type Photo = { src: string; label: string; tag: string };

const photos: Photo[] = [
  { src: "/images/home-datacenter-racks-optimized.jpg", label: "Suite aisle · A-17", tag: "Colocation floor" },
  { src: "/global-network-map-optimized.jpg", label: "Global routes", tag: "Network reach" },
  { src: "/images/about-hero-optimized.jpg", label: "Operations desk", tag: "On-site engineering" },
  { src: "/images/about-hero-glasses-optimized.jpg", label: "Controlled access", tag: "Biometric entry" },
];

export function FacilityGallery() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const count = photos.length;
  const isOpen = openIndex !== null;

  const close = () => setOpenIndex(null);
  const step = (delta: number) =>
    setOpenIndex((prev) => (prev === null ? prev : (prev + delta + count) % count));

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen]);

  return (
    <section className="lax1-gallery" aria-label="LAX1 facility photos">
      <div className="lax1-band-head">
        <p className="lax1-section-kicker">Facility gallery</p>
        <h2>Inside LAX1.</h2>
      </div>

      <div className="lax1-gallery-grid">
        {photos.map((photo, index) => (
          <button
            type="button"
            className="lax1-gallery-tile"
            key={photo.label}
            onClick={() => setOpenIndex(index)}
            aria-label={`Enlarge ${photo.label}`}
          >
            <img src={photo.src} alt={photo.label} loading="lazy" />
            <span className="lax1-gallery-cap">
              <b>{photo.label}</b>
              <small>{photo.tag}</small>
            </span>
            <span className="lax1-gallery-zoom" aria-hidden="true">
              &#43;
            </span>
          </button>
        ))}
      </div>

      {isOpen ? (
        <div
          className="lax1-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={photos[openIndex].label}
          onClick={close}
        >
          <div className="lax1-lightbox-inner" onClick={(event) => event.stopPropagation()}>
            <button className="lax1-lightbox-close" onClick={close} aria-label="Close">
              &times;
            </button>
            <button
              className="lax1-lightbox-nav prev"
              onClick={() => step(-1)}
              aria-label="Previous photo"
            >
              &#8249;
            </button>
            <img src={photos[openIndex].src} alt={photos[openIndex].label} />
            <button
              className="lax1-lightbox-nav next"
              onClick={() => step(1)}
              aria-label="Next photo"
            >
              &#8250;
            </button>
            <div className="lax1-lightbox-cap">
              <b>{photos[openIndex].label}</b>
              <span>
                {photos[openIndex].tag} · {openIndex + 1} / {count}
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

type Source = { id: string; name: string; latency: number };
const sources: Source[] = [
  { id: "hk1", name: "Hong Kong · HK1", latency: 71 },
  { id: "jp1", name: "Tokyo · JP1", latency: 58 },
  { id: "sg1", name: "Singapore", latency: 92 },
];
const tools = ["Ping", "Traceroute", "MTR"] as const;
type Tool = (typeof tools)[number];
const networkPartners = [
  { src: "/images/networkpartners/ntt.svg", label: "NTT" },
  { src: "/images/networkpartners/china-telecom.svg", label: "China Telecom" },
  { src: "/images/networkpartners/china-mobile.svg", label: "China Mobile" },
  { src: "/images/networkpartners/china-unicom.svg", label: "China Unicom" },
  { src: "/images/networkpartners/Hurricane_Electric.svg", label: "Hurricane Electric" },
  { src: "/images/networkpartners/cogent.svg", label: "Cogent" },
  { src: "/images/networkpartners/cloudflare.svg", label: "Cloudflare" },
  { src: "/images/networkpartners/equinix.svg", label: "Equinix" },
  { src: "/images/networkpartners/google.svg", label: "Google" },
  { src: "/images/networkpartners/cogent (1).svg", label: "Cogent" },
];

function buildOutput(tool: Tool, source: Source, host: string) {
  const ms = (n: number) => `${n.toFixed(1)} ms`;
  const jitter = source.latency * 0.03;
  if (tool === "Ping") {
    return [
      `PING ${host} from ${source.name}`,
      `64 bytes  seq=1  time=${ms(source.latency)}`,
      `64 bytes  seq=2  time=${ms(source.latency + jitter)}`,
      `64 bytes  seq=3  time=${ms(source.latency - jitter)}`,
      ``,
      `3 packets transmitted, 3 received, 0% loss`,
      `rtt min/avg/max = ${(source.latency - jitter).toFixed(1)}/${source.latency.toFixed(1)}/${(source.latency + jitter).toFixed(1)} ms`,
    ];
  }
  if (tool === "Traceroute") {
    return [
      `traceroute to ${host}, 30 hops max`,
      ` 1  10.0.0.1              0.3 ms`,
      ` 2  ${source.id}-core.esited    1.8 ms`,
      ` 3  one-wilshire-ix     ${ms(source.latency - 9)}`,
      ` 4  ${host}      ${ms(source.latency)}  ok`,
    ];
  }
  return [
    `mtr — ${source.name} → LAX1 (${host})`,
    ` 1.  10.0.0.1              0.3 ms`,
    ` 2.  ${source.id}-core.esited      1.8 ms`,
    ` 3.  one-wilshire-ix     ${ms(source.latency - 9)}`,
    ` 4.  ${host}       ${ms(source.latency)}  ok`,
  ];
}

export function LookingGlass() {
  const [sourceId, setSourceId] = useState(sources[0].id);
  const [tool, setTool] = useState<Tool>("MTR");
  const [target, setTarget] = useState("");
  const [host, setHost] = useState("199.87.232.21");

  const source = sources.find((item) => item.id === sourceId) ?? sources[0];
  const output = useMemo(() => buildOutput(tool, source, host), [tool, source, host]);
  const down = Math.round(1000 - source.latency * 0.8);
  const up = Math.round(960 - source.latency * 0.7);

  const run = () => {
    const trimmed = target.trim();
    if (trimmed) setHost(trimmed);
  };

  return (
    <section className="lax1-lg">
      <div className="lax1-band-head">
        <p className="lax1-section-kicker">Network diagnostics sample</p>
      </div>

      <form
        className="lax1-lg-toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          run();
        }}
      >
        <div className="lax1-endpoints-strip" id="network-tests" aria-label="Test endpoints">
          <div className="lax1-endpoint">
            <span>LAX1 test IPv4</span>
            <b>199.87.232.21</b>
          </div>
          <div className="lax1-endpoint">
            <span>LAX1 test IPv6</span>
            <b>2604:4480::5</b>
          </div>
          <div className="lax1-endpoint">
            <span>Live network tests</span>
            <b className="accent">Contact support</b>
          </div>
        </div>
        <div className="lax1-lg-controls">
          <div className="lax1-lg-row">
            <label className="lax1-lg-source">
              <span className="sr-only">Source node</span>
              <select value={sourceId} onChange={(event) => setSourceId(event.target.value)}>
                {sources.map((item) => (
                  <option key={item.id} value={item.id}>
                    Source · {item.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="lax1-lg-tabs" role="tablist" aria-label="Test type">
              {tools.map((item) => (
                <button
                  type="button"
                  key={item}
                  role="tab"
                  aria-selected={tool === item}
                  className={tool === item ? "active" : ""}
                  onClick={() => setTool(item)}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          <div className="lax1-lg-target-group">
            <input
              className="lax1-lg-target"
              value={target}
              onChange={(event) => setTarget(event.target.value)}
              placeholder="target host or IP"
              aria-label="Target host or IP"
            />
            <button type="submit" className="lax1-lg-run">
              Show sample
            </button>
          </div>
        </div>
      </form>

      <div className="lax1-lg-body">
        <aside className="lax1-dc-intro" aria-label="LAX1 facility overview">
          <p className="lax1-section-kicker">LAX1 facility overview</p>
          <table className="lax1-dc-table">
            <tbody>
              <tr>
                <th scope="row">Security</th>
                <td>24/7 manned security and facility patrols</td>
              </tr>
              <tr>
                <th scope="row">Access</th>
                <td>Biometric palm scan and keycard entry</td>
              </tr>
              <tr>
                <th scope="row">Power</th>
                <td>35 MW diverse feed, redundant UPS</td>
              </tr>
              <tr>
                <th scope="row">Connectivity</th>
                <td>Under 500 ft conduit to One Wilshire</td>
              </tr>
              <tr>
                <th scope="row">Footprint</th>
                <td>489,722 sq ft, 100% data center</td>
              </tr>
              <tr>
                <th scope="row">Support</th>
                <td>24/7 on-site operating engineers</td>
              </tr>
            </tbody>
          </table>
        </aside>

        <div className="lax1-lg-terminal" aria-live="polite">
          <div className="lax1-lg-term-head">
            <span className="lax1-lg-dots" aria-hidden="true">
              <i></i>
              <i></i>
              <i></i>
            </span>
            <span>
              {tool.toLowerCase()} — {source.name} → LAX1 ({host})
            </span>
          </div>
          <pre>{output.join("\n")}</pre>
          <div className="lax1-lg-metrics">
            <b className="down">&#8595; {down} Mbps</b>
            <b className="up">&#8593; {up} Mbps</b>
            <span>Simulated results. No live measurements.</span>
          </div>
        </div>
      </div>

      <section className="lax1-partners" aria-label="Transit providers">
        <div className="lax1-partners-head">
          <span>Transit Providers</span>
        </div>
        <div className="lax1-partner-marquee" aria-hidden="true">
          <div className="lax1-partner-track">
            {[...networkPartners, ...networkPartners].map((partner, index) => (
              <img src={partner.src} alt="" key={`${partner.label}-${index}`} />
            ))}
          </div>
        </div>
      </section>
    </section>
  );
}
